import React, { useRef } from "react"; 
import { useReactToPrint } from "react-to-print";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Printer, DollarSign, AlertCircle, Receipt } from "lucide-react";
import { format } from "date-fns";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ExportMenu } from "../shared/ExportMenu";
import { Badge } from "@/components/ui/badge";
import { getClient, getApartment } from "@/utils/salesHelpers";
import { getPaymentOverdueStatus } from "@/utils/paymentHelpers";

export default function SalesReport({ sales, clients, apartments, payments, defaultedSales = [], dateRange, unfilteredSales = sales }) {
  // A payment's sale may fall outside the current date filter (e.g. a
  // monthly installment paid this month for a sale made months ago), so
  // this looks the sale up against the full list, not the filtered one.
  const isPaymentOverdue = (payment) => {
    const sale =
      unfilteredSales.find(s => s.id === payment.sale_id) ||
      defaultedSales.find(s => s.id === payment.sale_id);
    return getPaymentOverdueStatus(sale).isDelayed;
  };

  // Helper to check if a sale has any delayed payments
  const hasDelayedPayments = (saleId) => {
    return payments.some(p => p.sale_id === saleId && p.is_delayed);
  };
  
  const getDelayedPaymentCount = (saleId) => {
    return payments.filter(p => p.sale_id === saleId && p.is_delayed).length;
  };

  const getPaymentsForSale = (saleId) => {
    return payments.filter(p => p.sale_id === saleId);
  };

  const getTotalPaymentsForSale = (saleId) => {
    return payments
      .filter(p => p.sale_id === saleId)
      .reduce((sum, p) => sum + (p.amount || 0), 0);
  };
  const getCurrentMonthPaymentAmount = (sale) => {
  if (!sale.payment_start_date || !sale.payment_frequency_months) {
    return sale.monthly_payment || 0;
  }

  const startDate = new Date(sale.payment_start_date);
  const now = new Date();
  
  const monthsDiff = 
    (now.getFullYear() - startDate.getFullYear()) * 12 + 
    (now.getMonth() - startDate.getMonth());
  
  const frequency = sale.payment_frequency_months || 1;
  
  if (monthsDiff % frequency === 0) {
    return sale.monthly_payment || 0;
  }
  return 0;
};
  // FEATURE 3: Calculate overall payment status for a sale
  const getPaymentStatusLabel = (sale) => {
  if (!sale || sale.status === 'completed' || sale.status === 'defaulted') return 'Paid';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let expectedPaid = sale.first_installment || 0;
  if (sale.monthly_payment && sale.payment_duration_months && sale.sale_date) {
    const paymentFrequency = sale.payment_frequency_months || 1;
    const saleDate = new Date(sale.sale_date);
    saleDate.setHours(0, 0, 0, 0);
    const firstPaymentDate = new Date(saleDate);
    firstPaymentDate.setMonth(firstPaymentDate.getMonth() + paymentFrequency);
    const paymentEndDate = new Date(firstPaymentDate);
    paymentEndDate.setMonth(paymentEndDate.getMonth() + sale.payment_duration_months);
    let currentDate = new Date(firstPaymentDate);
    while (currentDate < today && currentDate <= paymentEndDate) {
      expectedPaid += sale.monthly_payment;
      currentDate.setMonth(currentDate.getMonth() + paymentFrequency);
    }
  }
  const actualPaid = sale.total_paid || 0;
  if (actualPaid < expectedPaid) {
    return 'Pending'; // Changed from 'Payment Pending'
  }
  return 'Paid'; // Changed from 'Paid Up'
};

  // Calculate metrics (including defaulted sales)
  const allSales = [...sales, ...defaultedSales];
  const totalValue = sales.reduce((sum, s) => sum + (s.total_price || 0), 0);
  const totalCollected = sales.reduce((sum, s) => sum + (s.total_paid || 0), 0);
  const avgSaleValue = sales.length > 0 ? totalValue / sales.length : 0;
  const totalDefaultedPaid = defaultedSales.reduce((sum, s) => sum + (s.total_paid || 0), 0);

  const statusConfig = {
    active: { label: 'Active', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    completed: { label: 'Completed', color: 'bg-green-100 text-green-700 border-green-200' },
    defaulted: { label: 'Defaulted', color: 'bg-red-100 text-red-700 border-red-200' },
  };

  // Build export data with payment information
  const exportData = [
    ...sales.map(sale => {
      const client = getClient(sale.client_id, clients);
      const rawBalance = (sale.total_price || 0) - (sale.total_paid || 0);
      const balance = sale.status === 'completed' ? 0 : (Number.isFinite(rawBalance) ? rawBalance : 0);
      const delayedCount = getDelayedPaymentCount(sale.id);
      const salePayments = getPaymentsForSale(sale.id);
      const totalPayments = getTotalPaymentsForSale(sale.id);
      
      return {
        sale_date: format(new Date(sale.sale_date), 'yyyy-MM-dd'),
        client: client?.full_name || 'N/A',
        contract_number: sale.contract_number || '',
        total_price: sale.total_price ?? 0,
        currency: sale.currency,
        advanced_payment: sale.first_installment ?? 0,
        monthly_payment: sale.monthly_payment ?? 0,
        total_paid: sale.total_paid ?? 0,
        balance,
        payment_method: sale.payment_method,
        status: sale.status,
        payment_count: salePayments.length,
        total_payments_received: totalPayments,
        delayed_payments: delayedCount,
        payment_status: getPaymentStatusLabel(sale), // FEATURE 3: Added Payment Status label
      };
    }),
    ...defaultedSales.map(sale => {
      const client = getClient(sale.client_id, clients);
      
      return {
        sale_date: format(new Date(sale.sale_date), 'yyyy-MM-dd'),
        client: client?.full_name || 'N/A',
        contract_number: sale.contract_number || '',
        total_price: sale.total_price,
        currency: sale.currency,
        advanced_payment: sale.first_installment || 0,
        monthly_payment: sale.monthly_payment || 0,
        monthly_payment_current: 0,
        total_paid: sale.total_paid || 0,
        balance: (sale.total_price || 0) - (sale.total_paid || 0),
        payment_method: sale.payment_method,
        status: 'Defaulted',
        payment_count: 0,
        total_payments_received: 0,
        delayed_payments: 0,
        payment_status: 'Paid Up', // Defaulted sales are considered closed
        defaulted_date: format(new Date(sale.defaulted_date), 'yyyy-MM-dd'),
      };
    })
  ];

  // Build detailed payment export data (one row per payment)
  const paymentExportData = payments.map(payment => {
    // A payment's date range and its parent sale's date range are filtered
    // independently — a monthly installment paid this month can belong to a
    // sale made months ago, so look the sale up in the *full* list, not the
    // date-filtered one, or it comes up empty even though the sale exists.
    const sale = unfilteredSales.find(s => s.id === payment.sale_id);
    const client = sale ? getClient(sale.client_id, clients) : null;
    const apartment = sale ? getApartment(sale.apartment_id, apartments) : null;
    
    return {
      payment_date: format(new Date(payment.payment_date), 'yyyy-MM-dd'),
      client: client?.full_name || 'N/A',
      contract_number: sale?.contract_number || '-',
      unit: apartment?.unit_number || 'N/A',
      amount: payment.amount,
      currency: payment.currency || 'USD',
      payment_method: payment.payment_method,
      payment_type: payment.payment_type || '',
      reference_number: payment.reference_number || '',
      is_delayed: payment.is_delayed ? 'Yes' : 'No',
      delay_days: payment.delay_days || 0,
      notes: payment.notes || '',
    };
  });

  // Combined export: One row per sale with payment details
  const combinedExportColumns = [
    { header: 'Sale Date', key: 'sale_date' },
    { header: 'Client', key: 'client' },
    { header: 'Unit', key: 'unit' },
    { header: 'Contract No.', key: 'contract_number' },
    { header: 'Payment Method', key: 'payment_method' },
    { header: 'Unit Price', key: 'total_price' },
    { header: 'Currency', key: 'currency' },
    { header: 'Monthly Payment', key: 'monthly_payment' },
    { header: 'Payment Status', key: 'payment_status' },
    { header: 'Advance', key: 'advanced_payment' },
    { header: 'Total Instalment Paid', key: 'total_paid' },
    { header: 'Remaining Balance', key: 'balance' },
    { header: 'Sale Status', key: 'status' },
    { header: 'Notes', key: 'notes' }
  ];

const combinedExportData = [
  ...sales.map(sale => {
    const client = getClient(sale.client_id, clients);
    const apartment = getApartment(sale.apartment_id, apartments);
    const rawBalance = (sale.total_price || 0) - (sale.total_paid || 0);
    const balance = sale.status === 'completed' ? 0 : (Number.isFinite(rawBalance) ? rawBalance : 0);
    const delayedCount = getDelayedPaymentCount(sale.id);
    const salePayments = getPaymentsForSale(sale.id);
    
    // Calculate total delayed days for this sale
    const totalDelayedDays = salePayments
      .filter(p => p.is_delayed)
      .reduce((sum, p) => sum + (p.delay_days || 0), 0);

    return {
      sale_date: format(new Date(sale.sale_date), 'yyyy-MM-dd'),
      client: client?.full_name || 'N/A',
      contract_number: sale.contract_number || '',
      unit: apartment?.unit_number || 'N/A',
      total_price: sale.total_price ?? 0,
      currency: sale.currency,
      advanced_payment: sale.first_installment ?? 0,
      monthly_payment: getCurrentMonthPaymentAmount(sale),
      payment_duration_months: sale.payment_duration_months || '',
      total_paid: sale.total_paid ?? 0,
      balance,
      payment_count: salePayments.length,
      payment_status: getPaymentStatusLabel(sale), // Now returns only 'Paid' or 'Pending'
      delayed_days: totalDelayedDays,
      status: sale.status,
      payment_method: sale.payment_method || 'Cash', // Added
      notes: sale.notes || '' // Added Sale Notes
    };
  }),
  ...defaultedSales.map(sale => {
    const client = getClient(sale.client_id, clients);
    const apartment = getApartment(sale.apartment_id, apartments);
    return {
      sale_date: format(new Date(sale.sale_date), 'yyyy-MM-dd'),
      client: client?.full_name || 'N/A',
      contract_number: sale.contract_number || '',
      unit: apartment?.unit_number || 'N/A',
      total_price: sale.total_price,
      currency: sale.currency,
      advanced_payment: sale.first_installment || 0,
      monthly_payment: getCurrentMonthPaymentAmount(sale),
      payment_duration_months: sale.payment_duration_months || '',
      total_paid: sale.total_paid || 0,
      balance: (sale.total_price || 0) - (sale.total_paid || 0),
      payment_count: 0,
      payment_status: 'Paid', 
      delayed_days: 0,
      status: 'Defaulted',
      payment_method: sale.payment_method || 'Cash',
      notes: sale.notes || ''
    };
  })
];

  const contentRef = useRef(null);
  const reactToPrintFn = useReactToPrint({ contentRef });

  return (
    <div ref={contentRef} className="space-y-6">
      {/* Header */}
      <Card className="shadow-lg border-0">
        <CardHeader>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <CardTitle className="text-2xl font-bold flex items-center gap-2">
                <DollarSign className="w-6 h-6" />
                Sales & Payments Report {/* Updated Title */}
              </CardTitle>
              <p className="text-sm text-slate-600 mt-1">
                Generated on {format(new Date(), 'PPP')}
              </p>
            </div>
            <div className="flex gap-2 print:hidden">
              {/* SINGLE COMBINED EXPORT BUTTON */}
              <ExportMenu
                data={combinedExportData} 
                columns={combinedExportColumns} 
                title="Sales & Payments Report"
                filename={`sales_and_payments_report_${new Date().toISOString().split('T')[0]}`}
                buttonLabel="Export Sales & Payments"
                dateField="sale_date" 
              />
              <Button variant="outline" onClick={() => reactToPrintFn()} className="gap-2">
                <Printer className="w-4 h-4" />
                Print
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Summary Stats */}
      <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <Card className="shadow-md border-0">
          <CardContent className="pt-6">
            <p className="text-sm text-slate-600 mb-1">Total Sales</p>
            <p className="text-4xl font-bold text-slate-900">{sales.length}</p>
          </CardContent>
        </Card>
        <Card className="shadow-md border-0">
          <CardContent className="pt-6">
            <p className="text-sm text-slate-600 mb-1">Total Value</p>
            <p className="text-3xl font-bold text-green-600">{totalValue.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="shadow-md border-0">
          <CardContent className="pt-6">
            <p className="text-sm text-slate-600 mb-1">Collected</p>
            <p className="text-3xl font-bold text-blue-600">{totalCollected.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="shadow-md border-0">
          <CardContent className="pt-6">
            <p className="text-sm text-slate-600 mb-1">Avg Sale Value</p>
            <p className="text-3xl font-bold text-purple-600">{avgSaleValue.toFixed(0)}</p>
          </CardContent>
        </Card>
        <Card className="shadow-md border-0 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-sm text-red-600 mb-1">Defaulted Sales</p>
            <p className="text-4xl font-bold text-red-700">{defaultedSales.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Payment Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="shadow-md border-0">
          <CardContent className="pt-6">
            <p className="text-sm text-slate-600 mb-1">Total Payments</p>
            <p className="text-3xl font-bold text-slate-900">{payments.length}</p>
          </CardContent>
        </Card>
        <Card className="shadow-md border-0">
          <CardContent className="pt-6">
            <p className="text-sm text-slate-600 mb-1">Total Payment Amount</p>
            <p className="text-3xl font-bold text-green-600">
              {payments.reduce((sum, p) => sum + (p.amount || 0), 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card className="shadow-md border-0 bg-green-50">
          <CardContent className="pt-6">
            <p className="text-sm text-green-600 mb-1">On Time Payments</p>
            <p className="text-3xl font-bold text-green-700">
              {payments.filter(p => !isPaymentOverdue(p)).length}
            </p>
          </CardContent>
        </Card>
        <Card className="shadow-md border-0 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-sm text-red-600 mb-1">Delayed Payments</p>
            <p className="text-3xl font-bold text-red-700">
              {payments.filter(p => isPaymentOverdue(p)).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Sales Table */}
      <Card className="shadow-lg border-0">
        <CardHeader>
          <CardTitle>Complete Sales List</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
              <TableRow>
                <TableHead>Sale Date</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead>Contract No.</TableHead>
                <TableHead>Payment Method</TableHead>
                <TableHead>Unit Price</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead>Monthly Payment</TableHead>
                <TableHead>Payment Status</TableHead>
                <TableHead>Advance</TableHead>
                <TableHead>Total Instalment Paid</TableHead>
                <TableHead>Remaining Balance</TableHead>
                <TableHead>Sale Status</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
              <TableBody>
                {sales.map(sale => {
                  const client = getClient(sale.client_id, clients);
                  const rawBalance = (sale.total_price || 0) - (sale.total_paid || 0);
                  const balance = sale.status === 'completed' ? 0 : (Number.isFinite(rawBalance) ? rawBalance : 0);
                  const paymentStatus = getPaymentStatusLabel(sale);
                  
                  return (
                    <TableRow key={sale.id}>
                      <TableCell>{format(new Date(sale.sale_date), 'MMM d, yyyy')}</TableCell>
                      <TableCell className="font-semibold">{client?.full_name || 'N/A'}</TableCell>
                      <TableCell>{getApartment(sale.apartment_id, apartments)?.unit_number || 'N/A'}</TableCell>
                      <TableCell>{sale.contract_number || '-'}</TableCell>
                      <TableCell>{sale.payment_method || 'Cash'}</TableCell>
                      <TableCell>{(sale.total_price ?? 0).toLocaleString()}</TableCell>
                      <TableCell>{sale.currency || 'USD'}</TableCell>
                      <TableCell className="font-semibold">
                        {getCurrentMonthPaymentAmount(sale).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {paymentStatus === 'Pending' ? (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-200 border flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            Pending
                          </Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700 border-green-200 border">
                            Paid
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-blue-600 font-semibold">
                        {(sale.first_installment ?? 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-green-600 font-semibold">
                        {(sale.total_paid ?? 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-amber-600 font-semibold">
                        {balance.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusConfig[sale.status]?.color} border`}>
                          {statusConfig[sale.status]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell>{sale.notes || ''}</TableCell>
                    </TableRow>
                  );
                })}
                {defaultedSales.map(sale => {
                  const client = getClient(sale.client_id, clients);
                  const balance = (sale.total_price || 0) - (sale.total_paid || 0);
                  
                  return (
                    <TableRow key={sale.id} className="bg-red-50">
                      <TableCell>{format(new Date(sale.sale_date), 'MMM d, yyyy')}</TableCell>
                      <TableCell className="font-semibold">{client?.full_name || 'N/A'}</TableCell>
                      <TableCell>{getApartment(sale.apartment_id, apartments)?.unit_number || 'N/A'}</TableCell>
                      <TableCell>{sale.contract_number || '-'}</TableCell>
                      <TableCell>{sale.payment_method || 'Cash'}</TableCell>
                      <TableCell>{sale.total_price?.toLocaleString()}</TableCell>
                      <TableCell>{sale.currency || 'USD'}</TableCell>
                      <TableCell className="font-semibold">0</TableCell>
                      <TableCell>
                        <Badge className="bg-gray-100 text-gray-700 border-gray-200 border">
                          Paid
                        </Badge>
                      </TableCell>
                      <TableCell className="text-blue-600 font-semibold">
                        {(sale.first_installment || 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-red-600 font-semibold">
                        {(sale.total_paid || 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-amber-600 font-semibold">
                        {balance.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-red-100 text-red-700 border-red-200 border">
                          Defaulted
                        </Badge>
                      </TableCell>
                      <TableCell>{sale.notes || ''}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Payment Records Table */}
      <Card className="shadow-lg border-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            Payment Records
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Contract #</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                      No payment records found
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map(payment => {
                    // Same reasoning as paymentExportData above — look the
                    // sale up against the full list, not the date-filtered one.
                    const sale = unfilteredSales.find(s => s.id === payment.sale_id);
                    const client = sale ? getClient(sale.client_id, clients) : null;
                    const overdueStatus = getPaymentOverdueStatus(sale);
                    
                    return (
                      <TableRow key={payment.id} className={overdueStatus.isDelayed ? 'bg-red-50' : ''}>
                        <TableCell>{format(new Date(payment.payment_date), 'MMM d, yyyy')}</TableCell>
                        <TableCell className="font-semibold">{client?.full_name || 'N/A'}</TableCell>
                        <TableCell>{sale?.contract_number || '-'}</TableCell>
                        <TableCell className="text-green-600 font-semibold">
                          {(payment.amount || 0).toLocaleString()} {payment.currency || 'USD'}
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-blue-100 text-blue-700 border-blue-200 border">
                            {payment.payment_method}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {payment.payment_type ? payment.payment_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '-'}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {payment.reference_number || '-'}
                        </TableCell>
                        <TableCell>
                          {overdueStatus.isDelayed ? (
                            <Badge className="bg-red-100 text-red-700 border-red-200 border flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Overdue{overdueStatus.missedPayments > 0 ? ` - ${overdueStatus.missedPayments} missed` : ''}
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700 border-green-200 border">
                              On Time
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
    </div>
  );
}